<template>
  <vee-form v-slot="{ validate }" as="" :initial-errors="initialErrors">
    <form
      ref="formRef"
      class="standard-form"
      method="post"
      :action="formAction"
      enctype="multipart/form-data"
      novalidate
      @submit.prevent="handleSubmit(validate)"
    >
      <input name="_method" type="hidden" value="PUT" />

      <section class="content-group -bg-01 -center -vs-medium -vs-collapse-top -hs-large">
        <div class="group -full -flex -between">
          <div class="group -half">
            <!-- Profile -->
            <div class="group -edge -first -bg-04 -hs-medium -vs-medium">
              <form-fieldset-slot
                legend="Profile Information"
                legend-classes="heading -small demi"
                input-name=""
                :show-error-msg="false"
              >
                <text-input
                  v-model="user.firstName"
                  input-id="first_name"
                  label="First Name"
                  placeholder="First Name"
                  rules="required|max:50"
                  validate-as="First name"
                  maxlength="50"
                />

                <text-input
                  v-model="user.lastName"
                  input-id="last_name"
                  label="Last Name"
                  placeholder="Last Name"
                  validate-as="Last name"
                  rules="required|max:50"
                  maxlength="50"
                />

                <text-input
                  v-model="user.displayName"
                  input-id="display_name"
                  label="Display Name"
                  placeholder="Display Name"
                  rules="required|min:3"
                  validate-as="Display name"
                />

                <file-input
                  ref="fileInput"
                  type="file"
                  input-id="avatar_image_upload"
                  rules="image|size:5120"
                  :accept="mimeTypes.join(',')"
                  @change="onFileChange"
                >
                  <template #label>
                    <label for="avatar_image_upload">
                      <span class="file-drop-label">Member Profile Image</span>
                      <div class="file-drop">
                        <div v-if="avatarImage" class="default-avatar">
                          <div class="crop" :style="cropStyles"></div>
                          <span class="camera-icon"><span class="iconfont-camera" /></span>
                        </div>

                        <div v-else class="default-avatar">
                          <div class="avatar-placeholder demi">
                            <span>{{ user.displayName ? user.displayName.charAt(0) : '' }}</span>
                          </div>
                          <span class="camera-icon"><span class="iconfont-camera" /></span>
                        </div>
                      </div>
                      <span class="">File size should not exceed 5MB.</span>
                    </label>
                  </template>
                </file-input>
              </form-fieldset-slot>
            </div>

            <!-- Email Consent -->
            <div class="group -edge -last -bg-04 -hs-medium -vs-medium">
              <form-fieldset-slot
                legend="Email Preferences"
                legend-classes="heading -small demi"
                input-name=""
                :show-error-msg="false"
              >
                <text-input
                  v-model="user.email"
                  input-id="email"
                  type="email"
                  label="Email"
                  placeholder="Email Address"
                  rules="required|email|max:100|fbEmailUnique"
                  autocomplete="email"
                  :validate-aggressive="false"
                  maxlength="100"
                />

                <verify-email v-if="user.emailStatus !== 1" />

                <template v-for="emailConsent in props.emailConsents" :key="emailConsent.typeId">
                  <checkbox-input
                    v-model="state.emailConsents"
                    :input-id="`consent_${emailConsent.typeId}`"
                    name="emailConsents[]"
                    :checked-value="emailConsent.typeId"
                    unchecked-value=""
                    :disabled="emailConsent.typeId === 501"
                  >
                    <template #label>
                      <span class="-flex -column">
                        <span>{{ emailConsent.typeName }}</span>
                        <span v-if="emailConsent.typeId === 501" class="small">
                          System emails are required for order receipts, gift codes, and other
                          account and security messages like resetting a forgotten password, etc.
                        </span>
                        <span v-if="emailConsent.typeId === 502" class="small">
                          Content emails are optional messages that will give you the latest
                          information on content releases, programs and challenges, shorts, and
                          more.
                        </span>
                        <span v-if="emailConsent.typeId === 503" class="small">
                          Discount emails are optional messages to let you know about exclusive
                          discounts for FB Plus or other Fitness Blender related products and
                          services.
                        </span>
                        <span v-if="emailConsent.typeId === 504" class="small">
                          Community emails are optional messages that keep you up-to-date with
                          conversations in the community, FB news, messages from the trainers, as
                          well as opportunities to beta test new features and give feedback to the
                          FB Team.
                        </span>
                      </span>
                    </template>
                  </checkbox-input>
                </template>

                <p class="-tight">We DO NOT sell the personal contact information of our users.</p>
              </form-fieldset-slot>
            </div>
          </div>

          <div class="group -half">
            <!-- System Prefs -->
            <div class="group -edge -first -bg-02 -hs-medium -vs-medium">
              <form-fieldset-slot
                legend="System Preferences"
                legend-classes="heading -small demi"
                input-name=""
                :show-error-msg="false"
              >
                <select-input
                  v-model.number="user.completeEffects"
                  input-id="complete_effects"
                  label="Workout Complete Effects"
                  :options="[
                    { value: 1, label: 'Show Pop-Up with Confetti' },
                    { value: 2, label: 'Show Pop-Up without Confetti' },
                    { value: 0, label: 'Do Not Show Pop-Up' },
                  ]"
                  @change="confirmCompleteEffects"
                >
                  <template v-if="user.completeEffects === 0" #note>
                    <div class="input-note">
                      <p class="small">
                        <strong>Note</strong>: The pop-up will still display without confetti when
                        completing a program.
                      </p>
                    </div>
                  </template>
                </select-input>

                <select-input
                  v-model.number="user.showBurnEstimates"
                  input-id="show_burn_estimates"
                  label="Display Calorie Burn Estimates"
                  :options="[
                    { value: 1, label: 'Show Calories by Default' },
                    { value: 0, label: 'Hide Calories by Default' },
                  ]"
                />

                <select-input
                  v-if="showPayForwardInput"
                  v-model.number="user.showPaidForward"
                  input-id="show_paid_forward"
                  label="Display Pay it Forward Icon"
                  :options="[
                    { value: 1, label: 'Show Icon' },
                    { value: 0, label: 'Hide Icon' },
                  ]"
                >
                  <template v-if="user.showPaidForward === 1" #note>
                    <div class="input-note">
                      <p class="small">
                        <strong>Note</strong>: The Pay it Forward icon will show next to your avatar
                        on comments for 30 days after a successful Pay it Forward purchase.
                      </p>
                    </div>
                  </template>
                </select-input>

                <select-input
                  v-if="features && features.ACHIEVEMENTS"
                  v-model.number="user.showAchievements"
                  input-id="show_achievements"
                  label="Display Achievements"
                  :options="[
                    { value: 1, label: 'Yes' },
                    { value: 0, label: 'No' },
                  ]"
                />

                <select-input
                  v-if="features && features.ANALYTICS"
                  v-model.number="user.showAnalytics"
                  input-id="show_analytics"
                  label="Display Analytics"
                  :options="[
                    { value: 1, label: 'Yes' },
                    { value: 0, label: 'No' },
                  ]"
                />
              </form-fieldset-slot>
            </div>

            <!-- Player Prefs -->
            <div
              v-if="features && features.ADS_FREE && videoPlayers"
              class="group -edge -middle -bg-02 -hs-medium -vs-medium"
            >
              <form-fieldset-slot
                legend="Video Player Preference"
                legend-classes="heading -small demi"
                input-name=""
                :show-error-msg="false"
              >
                <p v-if="user.videoPlayer === 'adfree'" class="-tight">
                  <strong>Note</strong>: The default video player for FB Plus is the Ads Free
                  player. Please reference the
                  <a href="/fb-plus#fbp-sys-reqs">video system requirements</a> for supported
                  browsers and casting.
                </p>
                <p v-if="user.videoPlayer === 'adfree_beta'" class="-tight">
                  <strong>Note</strong>: The Ads Free Beta player is primarily used to test new
                  video features, and unexpected errors might occur. You can switch back to the
                  default Ads Free player at anytime.
                </p>
                <p v-else-if="user.videoPlayer === 'adfree_legacy'" class="-tight">
                  <strong>Note</strong>: The Ads Free Legacy player is used to support older first
                  generation Chromecast devices. It is recommended to use the default Ads Free
                  player for second / third generation Chromecast devices and Chromecast Ultra.
                </p>
                <p v-else-if="user.videoPlayer === 'youtube'" class="-tight">
                  <strong>Note</strong>: Ads cannot be disabled on the YouTube player and some FB
                  Plus videos require / will automatically switch to the Ads Free player.
                </p>
                <select-input
                  v-model="user.videoPlayer"
                  input-id="video_player"
                  label="Default Video Player"
                  :options="
                    Object.keys(videoPlayers).map((player) => ({
                      value: player,
                      label: videoPlayers[player],
                    }))
                  "
                  @change="confirmYoutube"
                >
                </select-input>
              </form-fieldset-slot>
            </div>

            <!-- Calendar Prefs -->
            <div class="group -edge -middle -bg-02 -hs-medium -vs-medium">
              <form-fieldset-slot
                legend="Calendar Preferences"
                legend-classes="heading -small demi"
                input-name=""
                :show-error-msg="false"
              >
                <select-input
                  v-model.number="user.startMonday"
                  input-id="start_monday"
                  label="Start Week On"
                  :options="[
                    { value: 0, label: 'Sunday' },
                    { value: 1, label: 'Monday' },
                  ]"
                />
                <select-input
                  v-model.number="user.calendarMonthSlotStyle"
                  input-id="calendar_month_slot_style"
                  label="Calendar Month Display Style"
                  :options="
                    Object.keys(calendarMonthSlotStyles).map((key) => ({
                      value: key,
                      label: calendarMonthSlotStyles[key],
                    }))
                  "
                >
                  <template v-if="user.calendarMonthSlotStyle !== 'bars'" #note>
                    <div class="input-note">
                      <p class="small">
                        <strong>Note</strong>: Some combinations of smaller screen size,
                        orientation, and zoom will only display bars regardless of your selection.
                      </p>
                    </div>
                  </template>
                </select-input>
                <select-input
                  v-model.number="user.showCalendarHover"
                  input-id="show_calendar_hover"
                  label="Enable Day Preview"
                  :options="[
                    { value: 0, label: 'No' },
                    { value: 1, label: 'Yes' },
                  ]"
                >
                  <template v-if="user.showCalendarHover === 1" #note>
                    <div class="input-note">
                      <p class="small">
                        <strong>Note</strong>: The day preview feature is available on devices with
                        a mouse or cursor. This feature is not available on mobile and touch-screen
                        devices.
                      </p>
                    </div>
                  </template>
                </select-input>
              </form-fieldset-slot>
            </div>

            <!-- Beta Prefs -->
            <!--            <div class="group -edge -last -bg-05 -hs-medium -vs-medium">-->
            <!--              <form-fieldset-slot-->
            <!--                legend="Beta Preferences"-->
            <!--                legend-classes="heading -small demi"-->
            <!--                input-name=""-->
            <!--                :show-error-msg="false"-->
            <!--              >-->
            <!--                <p class="-tight">-->
            <!--                  We are currently beta testing new workout filters. We would love for you to try it-->
            <!--                  out and give us your feedback, but you can switch back to the original if you-->
            <!--                  prefer. Note: In the future, these beta filters will replace the original one so-->
            <!--                  be sure to get your feedback in before we make the change.-->
            <!--                </p>-->

            <!--                <select-input-->
            <!--                  v-model.number="user.filterBeta"-->
            <!--                  input-id="some_field_beta"-->
            <!--                  label="Participate in Beta Workout Filters"-->
            <!--                  :options="[-->
            <!--                    { value: 1, label: 'Yes' },-->
            <!--                    { value: 0, label: 'No' },-->
            <!--                  ]"-->
            <!--                />-->
            <!--              </form-fieldset-slot>-->
            <!--            </div>-->
          </div>
        </div>
      </section>

      <section class="content-group -bg-01 -center -vs-medium -vs-collapse-top -hs-large">
        <div
          v-if="showTosInput"
          class="group -full -only -bg-04 -hs-small -vs-small -vs-collapse-bottom -flex -center"
        >
          <checkbox-input
            v-model="user.tos"
            input-id="tos"
            checked-value="agree"
            unchecked-value=""
            rules="required"
            validate-as="Terms of service"
          >
            <template #label>
              <span>
                I agree to the Fitness Blender
                <a href="/page/terms-of-use-agreement" target="_blank" rel="noopener noreferrer"
                  >terms of service</a
                >
                and
                <a href="/page/privacy-policy" target="_blank" rel="noopener noreferrer"
                  >privacy policy</a
                >
              </span>
            </template>
          </checkbox-input>
        </div>

        <div class="group -full -only -bg-04 -hs-small -vs-small -flex -center">
          <csrf-input />
          <submit-button
            :submitting="state.processing"
            :label="buttonText"
            submitting-label="Processing…"
            classes="-main -no-case"
            @submit-action="handleSubmit(validate)"
          />
        </div>
      </section>
    </form>
  </vee-form>
</template>

<script setup lang="ts">
import { ref, computed, onBeforeMount, onMounted, nextTick } from 'vue'
import { storeToRefs } from 'pinia'
import { Form as VeeForm, type PrivateFormContext } from 'vee-validate'
import { initExtraValidationRules, errorBagToInitialErrors } from '@/utils/forms/validation-rules'
import { useUserAuthStore } from '@/stores/user-auth'
import { useModalsStore } from '@/stores/modals'
import SubmitButton from '@/components/buttons/submit-button.vue'
import CsrfInput from '@/components/forms/csrf-input.vue'
import { trackEvent } from '@/utils/analytics'
import TextInput from '@/components/forms/text-input.vue'
import SelectInput from '@/components/forms/select-input.vue'
import FileInput from '@/components/forms/file-input.vue'
import CheckboxInput from '@/components/forms/checkbox-input.vue'
import type { EmailConsent } from '@/types/user-auth-types'
import type { ErrorBag } from '@/types/global-types'
import FormFieldsetSlot from '@/components/forms/form-fieldset-slot.vue'
import VerifyEmail from '@/components/users/verify-email.vue'

/** INLINE TYPES **/

/** PROPS **/
const props = withDefaults(
  defineProps<{
    errorBag?: ErrorBag | null
    buttonText?: string
    formAction?: string
    videoPlayers: Record<string, string>
    emailConsents: EmailConsent[]
    calendarMonthSlotStyles: Record<string, string>
    showPayForwardInput?: boolean
    // only need to show if social-only accounts, blender accounts agree on registration
    showTosInput?: boolean
    registerEvent?: boolean
    registerEventLabel?: string
  }>(),
  {
    errorBag: null,
    buttonText: 'Save Changes',
    formAction: '/my/account',
    showPayForwardInput: false,
    showTosInput: false,
    registerEvent: false,
    registerEventLabel: '',
  },
)

/** STATE **/
const userAuthStore = useUserAuthStore()
const { user, features } = storeToRefs(userAuthStore)
const { openSimpleModal } = useModalsStore()
const state = ref({
  image: '',
  emailConsents: props.emailConsents
    .filter((consent) => consent.consentGiven)
    .map((consent) => consent.typeId),
  processing: false,
})
const formRef = ref<HTMLElement | null>(null)

/** NON REACTIVE **/
const mimeTypes = ['image/png', 'image/jpeg']
const initialErrors = errorBagToInitialErrors(props.errorBag)

/** COMPUTED **/
const avatarImage = computed(() => state.value.image || user.value.avatar)

const cropStyles = computed(() => ({
  backgroundImage: `url(${avatarImage.value})`,
}))

/** LIFECYCLE **/
onBeforeMount(() => {
  // load custom fb* validation rules
  initExtraValidationRules()

  // analytics event
  if (props.registerEvent) {
    trackEvent({
      category: 'Sign Up',
      action: 'Free Account',
      label: props.registerEventLabel,
    })
  }
})

onMounted(() => {
  // focus on hash input (but not facebook #_=_ that comes when signing up with facebook
  if (window && window.location && window.location.hash && window.location.hash !== '#_=_') {
    const el = document.querySelector(window.location.hash)

    if (el && el instanceof HTMLElement) {
      el.scrollIntoView({ block: 'center' })
      el.focus()
    }
  }
})

/** METHODS **/
function confirmCompleteEffects() {
  if (user.value.completeEffects === 0) {
    openSimpleModal({
      type: 'STANDARD',
      id: 'complete-effects-modal',
      props: {
        heading: 'Workout Complete Effects',
        showFooter: true,
        bodySlot:
          'Please note FB Plus features like "custom trackers" cannot be entered and programs cannot be "quick completed" if the pop-up is disabled. The pop-up will still display without confetti when completing a program.',
      },
    })
  }
}

function confirmYoutube() {
  if (user.value.videoPlayer !== 'adfree') {
    let modalHeader = 'Confirm Legacy Player'

    if (user.value.videoPlayer === 'youtube') {
      modalHeader = 'Confirm YouTube Player'
    } else if (user.value.videoPlayer === 'adfree_beta') {
      modalHeader = 'Confirm Beta Player'
    }

    let modalSubheader =
      'Please note that the Ads Free Legacy player is used to support older first generation Chromecast devices. It is recommended to use the default Ads Free player for second / third generation Chromecast devices and Chromecast Ultra.'

    if (user.value.videoPlayer === 'youtube') {
      modalSubheader =
        'Please note that ads cannot be disabled on the YouTube player and some FB Plus videos require / will automatically switch to the Ads Free player. You can switch back to the Ads Free player at anytime.'
    } else if (user.value.videoPlayer === 'adfree_beta') {
      modalSubheader =
        'Please note that the Ads Free Beta player is primarily used to test new video features, and unexpected errors might occur. You can switch back to the default Ads Free player at anytime.'
    }

    openSimpleModal({
      type: 'STANDARD',
      id: 'complete-effects-modal',
      props: {
        heading: modalHeader,
        showFooter: true,
        bodySlot: modalSubheader,
      },
    })
  }
}

function onFileChange(e: InputEvent) {
  const target = (e.target || e.dataTransfer) as HTMLInputElement
  const files = target && target.files

  if (!files || !files.length) {
    return
  }

  createImage(files[0])
}

function createImage(file: any) {
  const reader = new window.FileReader()

  reader.onload = (e: ProgressEvent<FileReader>) => {
    if (e.target) {
      state.value.image = e.target.result as string
    }
  }

  reader.readAsDataURL(file)
}

async function handleSubmit(validate: PrivateFormContext['validate']) {
  // no double submit
  if (state.value.processing) {
    return
  }

  // validation
  state.value.processing = true

  const results = await validate()

  // invalid, stop
  if (!results.valid) {
    state.value.processing = false
    return
  }

  // traditional form submit
  if (formRef.value instanceof HTMLFormElement) {
    // wait until next tick to make sure v-model update
    await nextTick()
    formRef.value.submit()
  }
}
</script>
